import { useState, useEffect } from 'react';
import { useAuth } from "./api/auth";
import { BrowserRouter as Router } from 'react-router-dom';
import { Routes, Route, Navigate } from 'react-router-dom';
import {
  experimental_extendTheme as materialExtendTheme,
  Experimental_CssVarsProvider as MaterialCssVarsProvider,
  THEME_ID as MATERIAL_THEME_ID,
} from '@mui/material/styles';
import { CssVarsProvider as JoyCssVarsProvider } from '@mui/joy/styles';
import GlobalStyles from '@mui/joy/GlobalStyles';
import CssBaseline from '@mui/joy/CssBaseline';
import Container from '@mui/material/Container';
import useMediaQuery from '@mui/material/useMediaQuery';
import Tabs from '@mui/joy/Tabs';
import TabList from '@mui/joy/TabList';
import Tab from '@mui/joy/Tab';
import TabPanel from '@mui/joy/TabPanel';
import Login from './components/pages/Login';
import SignupClosed from './components/pages/SignupClosed';
import ResetPassword from './components/pages/ResetPassword';
import LlmPanel from './components/pages/LlmPanel.js';
import ObservePanel from './components/pages/ObservePanel.js';
import NumberingPanel from './components/pages/NumberingPanel.js';
import RegistrationsPanel from './components/pages/RegistrationsPanel.js';
import DeploymentsPanel from './components/pages/DeploymentsPanel.js';
import SetsPanel from './components/pages/SetsPanel.js';
import UsersPanel from './components/pages/UsersPanel.js';
import { useIsAdmin } from './api/use-is-admin.js';
import Header from './components/common/Header';
import MobileHeader from './components/common/MobileHeader';
import HeroPage from './components/pages/HeroPage';
import Privacy from './components/pages/Privacy';
import ErrorAlert from './components/common/ErrorAlert';
import { useTheme } from '@mui/joy/styles';
import SwaggerPage from './components/pages/Swagger';
import { AgentFileProvider, useAgentFileActions } from './context/AgentFileActionsContext.jsx';
import { SIGNUP_ENABLED } from './lib/signup-policy';


const materialTheme = materialExtendTheme();

function Home({ ...rest }) {
  const [selectedAgentId, setSelectedAgentId] = useState(null);
  // Build is now the Sets-based editor (tab value 5). The legacy single-agent
  // editor (LlmPanel, value 0) is kept mounted but is no longer in the tab bar.
  const [activeTab, setActiveTab] = useState(5);
  const [observeCallId, setObserveCallId] = useState(null);
  // "Troubleshoot with AI" seed handed from the Observe tab to the Build tab:
  // { agentId, testResult }. Observe gathers the call's transcripts + logs, sets
  // this, and jumps to Build; SetsPanel consumes it to open the AI builder.
  const [troubleshootSeed, setTroubleshootSeed] = useState(null);
  const { syncFileSession } = useAgentFileActions();
  const isAdmin = useIsAdmin();

  useEffect(() => {
    syncFileSession({ activeTab, selectedAgentId, setSelectedAgentId });
    return () => syncFileSession(null);
  }, [activeTab, selectedAgentId, setSelectedAgentId, syncFileSession]);

  // Hide Numbering/Registrations on mobile; if active, bounce back to Build.
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mq = window.matchMedia('(max-width:600px)');
    const enforce = () => {
      if (mq.matches && (activeTab === 3 || activeTab === 4 || activeTab === 6)) setActiveTab(5);
    };
    enforce();
    if (mq.addEventListener) mq.addEventListener('change', enforce);
    else mq.addListener(enforce);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', enforce);
      else mq.removeListener(enforce);
    };
  }, [activeTab]);

  return (
    <Container
      disableGutters
      sx={{
        pt: 'var(--Header-height)',
        mt: 2,
        px: { xs: 0, sm: 2 },
      }}
    >
      <Tabs
        value={activeTab}
        onChange={(_, v) => setActiveTab(v === null ? 5 : v)}
        sx={{
          borderRadius: 0,
        }}
      >
        <TabList
          sx={{
            borderRadius: 0,
            border: 'none',
            boxShadow: 'none',
            overflowX: { xs: 'auto', sm: 'visible' },
            overflowY: 'hidden',
            flexWrap: { xs: 'nowrap', sm: 'wrap' },
            whiteSpace: 'nowrap',
            WebkitOverflowScrolling: 'touch',
            scrollbarWidth: 'thin',
            '&::-webkit-scrollbar': { height: 6 },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: 'rgba(0,0,0,0.25)',
              borderRadius: 999,
            },
          }}
        >
          <Tab value={5} sx={{ flex: '0 0 auto' }}>Build</Tab>
          <Tab value={1} sx={{ flex: '0 0 auto' }}>Deploy</Tab>
          <Tab value={2} sx={{ flex: '0 0 auto', marginRight: { xs: 0, sm: 'auto' } }}>
            Observe
          </Tab>
          <Tab value={3} sx={{ flex: '0 0 auto', display: { xs: 'none', sm: 'flex' } }}>Numbering</Tab>
          <Tab value={4} sx={{ flex: '0 0 auto', display: { xs: 'none', sm: 'flex' } }}>Registrations</Tab>
          {isAdmin && (
            <Tab value={6} sx={{ flex: '0 0 auto', display: { xs: 'none', sm: 'flex' } }}>Users</Tab>
          )}
        </TabList>
        {/* Legacy single-agent editor: no longer in the tab bar, but kept mounted
            (value 0 is never the active tab) so any remaining background users of
            its registered file API keep working. Slated for full removal once the
            Sets-based Build tab fully replaces it. */}
        <TabPanel value={0} keepMounted sx={{ p: 0 }}>
          <LlmPanel
            {...rest}
            selectedAgentId={selectedAgentId}
            setSelectedAgentId={setSelectedAgentId}
            syncToAgentId={activeTab === 0 ? selectedAgentId : null}
            isBuildTabActive={activeTab === 0}
          />
        </TabPanel>
        <TabPanel value={1} sx={{ p: 0 }}>
          <DeploymentsPanel {...rest} selectedAgentId={selectedAgentId} />
        </TabPanel>
        <TabPanel value={2} sx={{ p: 0 }}>
          <ObservePanel
            {...rest}
            selectedAgentId={selectedAgentId}
            setSelectedAgentId={setSelectedAgentId}
            observeCallId={observeCallId}
            setObserveCallId={setObserveCallId}
            setActiveTab={setActiveTab}
            onTroubleshoot={(seed) => { setTroubleshootSeed(seed); setActiveTab(5); }}
          />
        </TabPanel>
        <TabPanel value={3} sx={{ p: 0, display: { xs: 'none', sm: 'block' } }}>
          <NumberingPanel
            {...rest}
            setSelectedAgentId={setSelectedAgentId}
            setActiveTab={setActiveTab}
          />
        </TabPanel>
        <TabPanel value={4} sx={{ p: 0, display: { xs: 'none', sm: 'block' } }}>
          <RegistrationsPanel
            {...rest}
          />
        </TabPanel>
        {isAdmin && (
          <TabPanel value={6} sx={{ p: 0, display: { xs: 'none', sm: 'block' } }}>
            <UsersPanel {...rest} />
          </TabPanel>
        )}
        {/* keepMounted so the selected set (and any in-progress "Build with AI"
            session) survive switching to another tab and back — SetsPanel holds
            that selection in local state, which a remount would otherwise reset. */}
        <TabPanel value={5} keepMounted sx={{ p: 0 }}>
          <SetsPanel {...rest} troubleshootSeed={troubleshootSeed} setTroubleshootSeed={setTroubleshootSeed} />
        </TabPanel>
      </Tabs>
    </Container>
  );
}


export default function App() {

  const status = useAuth();
  const [error, setError] = useState('');
  const [inform, setInform] = useState('');
  const [email, setEmail] = useState('notSent');
  const messages = { error, setError, inform, setInform };
  const uiMode = useMediaQuery('(prefers-color-scheme: dark)') ? 'dark' : 'light';
  const theme = useTheme();
  const small = useMediaQuery(theme.breakpoints.up('sm'));

  return (
    // Double CSS wrapping because MUI Joy is nice, but not feature complete:
    //  https://mui.com/joy-ui/guides/using-joy-ui-and-material-ui-together/
    <MaterialCssVarsProvider theme={{ [MATERIAL_THEME_ID]: materialTheme }}>
      <JoyCssVarsProvider
        // light is way more readable at xs so only honour pref at sm and above
        defaultMode={small ? uiMode : 'light'}
        disableTransitionOnChange
      >
        <CssBaseline />
        <GlobalStyles
          styles={{
            ':root': {
              '--Collapsed-breakpoint': '769px', // form will stretch when viewport is below `769px`
              '--Cover-width': '40vw', // must be `vw` only
              '--Form-maxWidth': '700px',
              '--Transition-duration': '0.4s', // set to `none` to disable transition
            },
          }}
        />

        {(status === "loading") && <></>}
        {(status !== "loading") &&
          <Router>
            <AgentFileProvider>
            <Header />
            <MobileHeader />
            <Routes>
              <Route path="/landing" element={
                status !== 'loggedIn'
                  ? <HeroPage {...messages} />
                  : <Navigate to="/" />
              } />
              <Route path="/login" element={
                status !== 'loggedIn'
                  ? <Login {...messages}  {...{ email, setEmail, status }} />
                  : <Navigate to="/" />
              } />
              <Route path="/interstitial" element={
                status !== 'loggedIn'
                  ? <Navigate to="/signup" replace />
                  : <Navigate to="/" />
              } />
              {/* With registration closed this is the referral page, not a form.
                  Kept on the same path so existing links, the Interstitial CTA
                  and a rejected OAuth popup all land somewhere that explains. */}
              <Route path="/signup" element={
                status !== 'loggedIn'
                  ? (SIGNUP_ENABLED
                    ? <Login variant="signup" {...messages} {...{ email, setEmail, status }} />
                    : <SignupClosed />)
                  : <Navigate to="/" />
              } />
              <Route path="/password-reset" element={
                status !== 'loggedIn' ?
                  <Login variant="lostPassword" {...messages} {...{ email, setEmail, status }} />
                  : <Navigate to="/" />
                } />
              <Route path="/reset-password" element={
                <ResetPassword {...messages} />
                } />
              <Route path="/" element={
                status === 'loggedIn'
                  ? <Home {...{ ...messages, status }} />
                  : <Navigate to="/landing" />
              } />
              <Route path="/privacy" element={<Privacy {...messages} {...{ email, setEmail, status }} />} />
              <Route path="/api" element={<SwaggerPage {...messages} />} />
            </Routes>
            </AgentFileProvider>
          </Router>
        }
        <ErrorAlert {...messages} />
      </JoyCssVarsProvider>
    </MaterialCssVarsProvider>
  );


}
